import type { ProposalOrder } from '@moment/horizon-data-access/proposal'
import type { CellClickedEvent } from 'ag-grid-community'

import { useCallback, useMemo } from 'react'

import Table from '@/components/atomic/table'
import { TableDataModel, TableSize } from '@/components/atomic/table/types'
import { useRatingColumns } from '@/hooks/use-rating-columns'
import { useGetProposalQuery } from '@/legacy-api/proposals-slice'
import { ProposalDetailsOrdersTableColumnId } from '@/types/enums/proposals'

import { getColumnDefinitions } from './columns'

interface BondSwapTableProps {
  proposalId: string
  onBondClick?: (event: CellClickedEvent) => void
  disablePagination?: boolean
  currentOrder?: ProposalOrder
  customOrders?: ProposalOrder[]
  onRemoveBond?: (bondId: string) => void
  originalOrderId?: string
  isAlternativeBondsTable?: boolean
  disableOriginalBond?: boolean
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function BondSwapTable({
  proposalId,
  onBondClick,
  disablePagination = false,
  currentOrder,
  customOrders,
  onRemoveBond,
  originalOrderId,
  isAlternativeBondsTable = false,
  disableOriginalBond = false,
}: BondSwapTableProps) {
  const { data: proposal } = useGetProposalQuery(
    { proposal_id: proposalId },
    { skip: !proposalId },
  )

  const orders = useMemo(() => {
    let baseOrders = []
    if (customOrders) {
      baseOrders = customOrders
    } else if (!proposal) {
      return []
    } else if (currentOrder) {
      // If currentOrder is provided, only show that order
      baseOrders = [currentOrder]
    } else {
      baseOrders = proposal.orders
    }

    // Flatten instrument ratings to root level for RatingCellRenderer
    return baseOrders.map((order) => ({
      ...order,
      sp_rating: order.instrument?.sp_rating,
      moodys_rating: order.instrument?.moodys_rating,
    }))
  }, [proposal, currentOrder, customOrders])

  const handleBondClick = useCallback(
    (event: CellClickedEvent) => {
      // If disableOriginalBond is true and the clicked row is the original bond, do nothing
      if (
        disableOriginalBond &&
        event.data?.moment_order_id === originalOrderId
      ) {
        return
      }
      onBondClick?.(event)
    },
    [onBondClick, disableOriginalBond, originalOrderId],
  )

  const baseColumnDefs = useMemo(() => {
    // Add an action column for removing bonds if onRemoveBond is provided
    const columns = getColumnDefinitions()

    // Find the SELECT column and update its context
    const selectColumn = columns.find(
      (col) =>
        col.colId === ProposalDetailsOrdersTableColumnId.PROPOSAL_ORDER_SELECT,
    )

    if (selectColumn) {
      selectColumn.context = {
        handleOrderRemoveAction: onBondClick
          ? (order: ProposalOrder) => {
              // If disableOriginalBond is true and the order is the original bond, do nothing
              if (
                disableOriginalBond &&
                order.moment_order_id === originalOrderId
              ) {
                return
              }
              // Create a simulation of CellClickedEvent with just the data property
              const event = { data: order } as unknown as CellClickedEvent
              onBondClick(event)
            }
          : undefined,
        // Only pass originalOrderId if not in alternative bonds table
        originalOrderId: isAlternativeBondsTable ? undefined : originalOrderId,
        // Only pass selectedOrderId if not in alternative bonds table
        selectedOrderId: isAlternativeBondsTable
          ? undefined
          : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
            customOrders && customOrders.length > 1
            ? customOrders.find(
                (order) => order.moment_order_id !== originalOrderId,
              )?.moment_order_id
            : undefined,
      }
    }

    if (onRemoveBond && originalOrderId) {
      const isSelectedBond = (bondId: string) => {
        if (!customOrders) return false
        return (
          customOrders.length > 1 &&
          customOrders.find(
            (order) => order.moment_order_id !== originalOrderId,
          )?.moment_order_id === bondId
        )
      }

      columns.push({
        headerName: '',
        field: 'moment_order_id',
        width: 70,
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        cellRenderer: (params: any) => {
          const isOriginalOrder = params.value === originalOrderId
          if (isOriginalOrder || isSelectedBond(params.value)) {
            return ''
          }
          return `<button class="text-xs text-primary-500 hover:text-primary-700 px-2 py-1 border border-primary-200 rounded-md">Clear</button>`
        },
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        onCellClicked: (params: any) => {
          const isOriginalOrder = params.value === originalOrderId
          if (!isOriginalOrder && !isSelectedBond(params.value)) {
            onRemoveBond(params.value)
          }
        },
      })
    }

    return columns
  }, [
    onRemoveBond,
    originalOrderId,
    onBondClick,
    customOrders,
    isAlternativeBondsTable,
    disableOriginalBond,
  ])

  const columnDefs = useRatingColumns(baseColumnDefs)

  return (
    <Table
      data={orders}
      tableSize={TableSize.Full}
      gridModel={TableDataModel.ClientSide}
      columnDefs={columnDefs}
      onCellClicked={handleBondClick}
      idConfig={{ clientSideField: 'moment_order_id' }}
      pagination={!disablePagination}
    />
  )
}
